import React, {useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Card,
    CircularProgress,
    List,
    ListItem, Tooltip,
    Typography,
    Chip
} from "@mui/material";
import {useQueryAsUser} from "../../clients/graphql/graphql.hooks";
import {Link} from "react-router-dom";
import _ from 'lodash';
import {ConnectedStatus} from "../../components/status/ConnectedStatus";
import {DisconnectedStatus} from "../../components/status/DisconnectedStatus";
import {
    InstitutionData,
    InstitutionPortfolio,
    listInstitutionsQuery
} from "../../clients/graphql/views/institutions-queries";
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import {useUserPortfolioLpoaFetch} from "../../clients/graphql/pages/lpoa-queries";
import {hasActiveLpoa} from "../../utils/lpoaStatus";

export type UserInstitutionsListProps = {
    userKey: number,
}
export function UserInstitutionsList({ userKey }: UserInstitutionsListProps) {

    const {loading, error, data} = useQueryAsUser(listInstitutionsQuery, { userKey, variables: { userKey } });
    
    // Fetch LPOA data for all portfolios
    const {data: lpoaData} = useUserPortfolioLpoaFetch({ userKey });

    const unsortedInstitutionList: InstitutionData[] = data?.institutionsGet?.institutions ?? [];
    const institutionList = _.sortBy(unsortedInstitutionList, ['connectionStatus']);
    return (
            <Box sx={{
                justifyContent: 'space-between',
                textAlign: 'center',
                height: '40rem',
                overflowY: 'scroll',
                scrollbarWidth: 'none',
                borderRadius: '8px',
                border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid lightgray',
                marginLeft: '.75rem',
                marginRight: '.75rem',
            }}
            >
                <List>
                    {
                        loading ? <CircularProgress/> :
                            error ? <Alert severity="warning">{error?.message}</Alert> :
                                institutionList.map((institution: InstitutionData, index: number) => <InstitutionDisplay key={index} userKey={userKey} institution={institution} lpoaData={lpoaData?.UserPortfolioLpoaFetch} />)
                    }
                </List>
            </Box>
    );
}

type InstitutionDisplayProps = {
    userKey: number,
    institution: InstitutionData,
    lpoaData?: any[],
}
function InstitutionDisplay({ userKey, institution, lpoaData }: InstitutionDisplayProps) {

    const [expanded, setExpanded] = useState<boolean>(false);
    
    // Helper function to determine effective connection status
    const getEffectiveConnectionStatus = () => {
        const institutionConnected = institution.connectionStatus === 'CONNECTED';
        const portfolios = institution.portfolios || [];
        
        if (institutionConnected) {
            return { status: 'CONNECTED', reason: 'institution' };
        }
        
        // Check if all portfolios are LPOA'd and connected
        const allPortfoliosLpoaAndConnected = portfolios.every(portfolio => {
            const portfolioLpoaData = lpoaData?.filter(lpoa => lpoa.portfolioKey === portfolio.portfolioKey) || [];
            const hasLpoa = hasActiveLpoa(portfolioLpoaData);
            return hasLpoa && portfolio.connectionStatus === 'CONNECTED';
        });
        
        if (allPortfoliosLpoaAndConnected && portfolios.length > 0) {
            return { status: 'CONNECTED', reason: 'lpoa' };
        }
        
        // Check if any portfolio is LPOA'd and connected (but not all)
        const hasLpoaConnectedPortfolio = portfolios.some(portfolio => {
            const portfolioLpoaData = lpoaData?.filter(lpoa => lpoa.portfolioKey === portfolio.portfolioKey) || [];
            const hasLpoa = hasActiveLpoa(portfolioLpoaData);
            return hasLpoa && portfolio.connectionStatus === 'CONNECTED';
        });
        
        if (hasLpoaConnectedPortfolio) {
            return { status: 'PARTIAL', reason: 'lpoa' };
        }
        
        return { status: 'DISCONNECTED', reason: 'institution' };
    };
    
    const effectiveStatus = getEffectiveConnectionStatus();

    return <Accordion expanded={expanded} onClick={() => setExpanded(!expanded)} >
        <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
            }}
            >
                <Typography 
                    component={Link} 
                    to={`/institution/${institution.institutionKey}`} 
                    fontSize={'small'}
                    sx={{
                        width: '40%',
                        display: 'inline-block', // Ensures proper block-level styling for the button
                        padding: '0.25rem 0.5rem', // Add some padding for spacing
                        color: (theme) => theme.palette.text.primary, // Text color
                        textDecoration: 'none', // Remove underline
                        border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[700]}`, // Black outline
                        borderRadius: '8px', // Slightly round corners for a button-like look
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.1)', // Subtle hover effect
                        },
                    }}
                >{institution.institutionName.split('_').map(_.capitalize).join(' ')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {effectiveStatus.status === 'CONNECTED' ? <ConnectedStatus/> :
                        effectiveStatus.status === 'PARTIAL' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip 
                                    label="LPOA" 
                                    size="small" 
                                    sx={{
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        fontWeight: 600,
                                        fontSize: '0.6rem',
                                        height: '20px',
                                        '& .MuiChip-label': {
                                            px: 0.5,
                                        },
                                    }}
                                />
                                <DisconnectedStatus/>
                            </Box>
                        ) : <DisconnectedStatus/>
                    }
                </Box>

                {( institution.connectionStatus !== 'DISCONNECTED' && institution.kycCheck.kycState === "MISMATCH")  && (
                    <Tooltip title={'KYC ' + institution.kycCheck.kycState}>
                        <WarningOutlinedIcon sx={{ marginLeft: '-5%' }} color='error'/>
                    </Tooltip>
                )}
                {( institution.connectionStatus !== 'DISCONNECTED' &&
                    (institution.kycCheck.kycState === "NEED_KYC" || institution.kycCheck.kycState === "MISSING_INSTITUTION_DATA")) && (
                    <Tooltip title={'KYC ' + institution.kycCheck.kycState}>
                        <WarningOutlinedIcon sx={{ marginLeft: '-5%' }} color='warning'/>
                    </Tooltip>
                )}
            </div>
        </AccordionSummary>
        <AccordionDetails>
            <InstitutionDetails userKey={userKey} institution={institution} lpoaData={lpoaData}/>
        </AccordionDetails>
    </Accordion>
}

type InstitutionDetailsProps = {
    userKey: number,
    institution: InstitutionData,
    lpoaData?: any[],
}
function InstitutionDetails({ institution, lpoaData }: InstitutionDetailsProps) {

    const portfolios = (institution?.portfolios ?? [])?.map((portfolio: InstitutionPortfolio) => {
        // Check if this portfolio has LPOA
        const portfolioLpoaData = lpoaData?.filter(lpoa => lpoa.portfolioKey === portfolio.portfolioKey) || [];
        const hasLpoa = hasActiveLpoa(portfolioLpoaData);
        
        return (
        <ListItem component={Link} to={`/portfolios/${portfolio.portfolioKey}`} disablePadding>
            <Card sx={{
                size: 'small',
                borderRadius: '8px',
                padding: '0.3rem',
                marginBottom: '0.5rem',
                border: (theme) => theme.palette.mode === 'dark' ? '1px solid white' : '1px solid gray',
                width: '100%',
                boxShadow: 'none',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)', // Subtle hover effect
                },
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{
                        fontSize: 'small',
                        color: (theme) => theme.palette.mode === 'dark' ? 'white' : 'black',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        {portfolio?.nickname ?? 'N/A'}
                        {hasLpoa && (
                            <Chip 
                                label="LPOA" 
                                size="small" 
                                sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontWeight: 600,
                                    fontSize: '0.6rem',
                                    height: '18px',
                                    '& .MuiChip-label': {
                                        px: 0.5,
                                    },
                                }}
                            />
                        )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{
                            color: portfolio.connectionStatus === 'CONNECTED' ? 'green' : 'red',
                            fontSize: '0.7rem',
                            fontWeight: 600
                        }}>
                            {portfolio.connectionStatus === 'CONNECTED' ? 'Connected' : 'Disconnected'}
                        </Typography>
                        <span style={{marginLeft: 'auto'}}>
                            {(portfolio?.accountId?.length > 8 ? '****' : '*'.repeat(Math.max(portfolio?.accountId?.length - 4, 0))) + portfolio?.accountId?.slice(-4)}
                        </span>
                    </Box>
                </Box>
            </Card>
        </ListItem>
        )
    });

    return <Box sx={{ textAlign: 'left', flexDirection: 'row', marginTop: '-0.5rem' }}>
        <Typography fontWeight={'bold'}>Portfolios:</Typography>
        <List>
            {portfolios}
        </List>
    </Box>
}


